// src/pages/CandidateAccount.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ping,
  candMe,
  getCandidateAccount,
  updateCandidateAccount,
  uploadCandidateAvatar,
  changeCandidatePassword,
  setCandidateToken,
  API_ROOT,
} from "../services/api";
import CareersNav from "../components/CareersNav";

function isValidLinkedInUrl(v) {
  if (!v) return true; // optional
  try {
    const u = new URL(v);
    return /(^|\.)linkedin\.com$/i.test(u.hostname);
  } catch {
    return false;
  }
}

// Make avatar URL absolute regardless of what the API returns.
function toAbsoluteAvatarUrl(u) {
  if (!u) return "";
  try {
    if (/^https?:\/\//i.test(u)) return u;
    const base =
      (process.env.REACT_APP_CAREERS_API_ROOT || "").trim() ||
      (window.__CAREERS_API_ROOT || "") ||
      // last-resort: if running dev on :3000 and API on :3010
      window.location.origin.replace(/:(\d+)$/, ":3010");
    return new URL(u.startsWith("/") ? u : `/${u}`, base).toString();
  } catch {
    return u;
  }
}

// turn account payload into local UI state
function toForm(c) {
  console.log('[CandidateAccount] toForm input:', c);
  console.log('[CandidateAccount] toForm fields:', {
    candidate_name: c?.candidate_name,
    candidate_email: c?.candidate_email,
    candidate_phone: c?.candidate_phone,
    candidate_linkedin_url: c?.candidate_linkedin_url,
    candidate_avatar: c?.candidate_avatar,
    candidate_avatar_url: c?.candidate_avatar_url,
    avatar_url: c?.avatar_url,
    verified: c?.verified
  });
  
  const formData = {
    name: c?.candidate_name || "",
    email: c?.candidate_email || "",
    phone: c?.candidate_phone || "",
    linkedin_url: c?.candidate_linkedin_url || "",
    avatar_url: toAbsoluteAvatarUrl(c?.candidate_avatar || c?.candidate_avatar_url || c?.avatar_url || ""),
    verified: !!c?.verified,
  };
  
  console.log('[CandidateAccount] toForm output:', formData);
  return formData;
}

export default function CandidateAccount() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const usp = useMemo(() => new URLSearchParams(search), [search]);

  const [org, setOrg] = useState(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    linkedin_url: "",
    avatar_url: "",
    verified: false,
  });

  // password change UI state
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [pwdBusy, setPwdBusy] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const p = await ping();
        setOrg(p?.organization || p?.org || null);

        const meResponse = await candMe();
        console.log('[CandidateAccount] candMe result:', meResponse);
        // candMe() returns { ok: true, candidate: {...} }
        const me = meResponse?.candidate;
        if (!me) {
          navigate("/", { replace: true });
          return;
        }
        
        const accountData = await getCandidateAccount();
        console.log('[CandidateAccount] getCandidateAccount result:', accountData);
        
        // ✅ FIX: Extract the candidate object from the response
        // getCandidateAccount returns { candidate: { candidate_name, ... } }
        const candidateInfo = accountData?.candidate || accountData;
        console.log('[CandidateAccount] Extracted candidate info:', candidateInfo);
        
        setForm(toForm(candidateInfo));
      } catch (e) {
        console.error('[CandidateAccount] Error loading account:', e);
        setErr(String(e.message || e));
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  async function onSave(ev) {
    ev.preventDefault();
    setErr("");
    setMsg("");
    if (!form.name.trim()) return setErr("Name is required");
    if (!form.email.trim()) return setErr("Email is required");
    if (!isValidLinkedInUrl(form.linkedin_url)) {
      return setErr("LinkedIn URL must be a valid linkedin.com URL (e.g., https://www.linkedin.com/in/your-handle)");
    }

    setSaving(true);
    try {
      const res = await updateCandidateAccount({
        name: form.name,
        email: form.email,
        phone: form.phone,
        linkedin_url: form.linkedin_url,
      });
      setMsg(res.message || "Profile updated");
      if (res.reverify) setMsg("Profile updated. Please verify your new email (check your inbox).");
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setSaving(false);
    }
  }

  async function onAvatarChange(ev) {
    const file = ev.target.files?.[0];
    if (!file) return;
    setErr("");
    setMsg("");
    try {
      const res = await uploadCandidateAvatar(file); // { avatar_url }
      const abs = toAbsoluteAvatarUrl(res.avatar_url || "");
      setForm((f) => ({ ...f, avatar_url: abs }));
    } catch (e) {
      setErr(String(e.message || e));
    }
  }

  async function onChangePassword(ev) {
    ev.preventDefault();
    setErr("");
    setMsg("");
    if (!pwd.current || !pwd.next || !pwd.confirm) {
      setErr("Fill all password fields");
      return;
    }
    if (pwd.next !== pwd.confirm) {
      setErr("New passwords do not match");
      return;
    }
    setPwdBusy(true);
    try {
      const res = await changeCandidatePassword({
        current_password: pwd.current,
        new_password: pwd.next,
      });
      setMsg(res.message || "Password updated");
      setPwd({ current: "", next: "", confirm: "" });
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setPwdBusy(false);
    }
  }

  function onLogout() {
    try {
      setCandidateToken(""); // clears localStorage
    } catch {}
    navigate(`/`, { replace: true });
  }

  return (
    <>
      <CareersNav org={org} />

      <div className="page-wrap" style={{ maxWidth: 760 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
          <h1 style={{ margin: 0 }}>My Account</h1>
          <button type="button" className="button" onClick={onLogout}>
            Log out
          </button>
        </div>

        {loading ? (
          <div>Loading…</div>
        ) : err ? (
          <div style={{ color: "#ef4444" }}>{String(err)}</div>
        ) : (
          <>
            {msg && (
              <div style={{ background: "#ecfeff", border: "1px solid #67e8f9", color: "#155e75", padding: 8, marginBottom: 12 }}>
                {msg}
              </div>
            )}

            <div className="grid-2" style={{ gap: 24 }}>
              <div>
                <form onSubmit={onSave}>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>Name</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      className="input"
                      style={{ width: "100%" }}
                      required
                    />
                  </div>

                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>Email</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      className="input"
                      style={{ width: "100%" }}
                      required
                    />
                    {form.verified ? (
                      <div style={{ color: "#16a34a", fontSize: 12, marginTop: 4 }}>Email verified</div>
                    ) : (
                      <div style={{ color: "#ef4444", fontSize: 12, marginTop: 4 }}>Email not verified</div>
                    )}
                  </div>

                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>Phone</label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                      className="input"
                      style={{ width: "100%" }}
                    />
                  </div>

                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>LinkedIn URL</label>
                    <input
                      type="url"
                      placeholder="https://www.linkedin.com/in/your-handle"
                      value={form.linkedin_url}
                      onChange={(e) => setForm((f) => ({ ...f, linkedin_url: e.target.value }))}
                      className="input"
                      style={{ width: "100%" }}
                    />
                    {form.linkedin_url && !isValidLinkedInUrl(form.linkedin_url) && (
                      <div style={{ color: "#ef4444", fontSize: 12, marginTop: 4 }}>
                        Please enter a valid linkedin.com URL.
                      </div>
                    )}
                    {form.linkedin_url && isValidLinkedInUrl(form.linkedin_url) && (
                      <div style={{ fontSize: 12, marginTop: 4 }}>
                        Optional. If provided, it must be under linkedin.com.
                      </div>
                    )}
                  </div>

                  <div>
                    <button type="submit" className="button" disabled={saving}>
                      {saving ? "Saving…" : "Save changes"}
                    </button>
                  </div>
                </form>
              </div>

              <div>
                <div style={{ marginBottom: 12, fontWeight: 600 }}>Profile picture</div>
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <div
                    style={{
                      width: 96,
                      height: 96,
                      borderRadius: "50%",
                      overflow: "hidden",
                      background: "#f1f5f9",
                    }}
                  >
                    {form.avatar_url ? (
                      <img
                        alt="avatar"
                        src={form.avatar_url}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        onError={(e) => {
                          e.currentTarget.src = "";
                          setForm((f) => ({ ...f, avatar_url: "" }));
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "grid",
                          placeItems: "center",
                          color: "#94a3b8",
                        }}
                      >
                        no photo
                      </div>
                    )}
                  </div>

                  <div>
                    <label htmlFor="avatarUpload" className="button" role="button" style={{ cursor: "pointer" }}>
                      Upload photo
                    </label>
                    <input id="avatarUpload" type="file" accept="image/*" style={{ display: "none" }} onChange={onAvatarChange} />
                    <div style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>JPG/PNG recommended. Square image looks best.</div>
                  </div>
                </div>

                <hr style={{ margin: "24px 0" }} />

                <form onSubmit={onChangePassword}>
                  <div style={{ marginBottom: 8, fontWeight: 600 }}>Change password</div>

                  <div style={{ marginBottom: 8 }}>
                    <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>Current password</label>
                    <input
                      type="password"
                      className="input"
                      value={pwd.current}
                      onChange={(e) => setPwd((s) => ({ ...s, current: e.target.value }))}
                      required
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>
                      New password
                    </label>
                    <input
                      type="password"
                      className="input"
                      value={pwd.next}
                      onChange={(e) => setPwd((s) => ({ ...s, next: e.target.value }))}
                      required
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontWeight: 600, marginBottom: 4 }}>
                      Confirm new password
                    </label>
                    <input
                      type="password"
                      className="input"
                      value={pwd.confirm}
                      onChange={(e) => setPwd((s) => ({ ...s, confirm: e.target.value }))}
                      required
                    />
                  </div>

                  <div style={{ marginTop: 12 }}>
                    <button type="submit" className="button" disabled={pwdBusy}>
                      {pwdBusy ? "Updating…" : "Update password"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
